import React, { useMemo, useState } from "react";
import { Button, Col, Form, Input, InputNumber, Modal, Pagination, Row, Space, Table, Typography, message } from "antd";
import type { PaginationProps, TableProps } from "antd";
import "./CombustionTable.css";
import { BsPencilSquare, BsTrashFill } from "react-icons/bs";
import { usePagination } from "src/hook/usePagination";
import { useDidMount } from "src/hook/useDidMount";
import { settingStore } from "src/stores/setting-store";
import { useShallow } from "zustand/react/shallow";
import { PlusCircleOutlined } from "@ant-design/icons";
import { generateErrorMessage } from "src/utils/error-message";
import VocsModal from "src/components/Modal";
import { debounce } from "lodash";
import { replaceUndefinedWithNull, trimAllKey } from "src/utils/utils-object";
import {
  CreateSettingFlareCompound,
  FilterSettingFlareCompound,
  ResponseSettingFlareCompound,
  UpdateSettingFlareCompound,
} from "src/entity/settings/flare-compound";
import { CommonSuccess } from "src/entity/common/menus-enum.enum";
import FormLabel from "src/components/AntdCustomPart/FormLabel/FormLabel";
import ModalTitle from "src/components/AntdCustomPart/ModalTitle/ModalTitle";
import CustomInputNumber from "src/components/AntdCustomPart/InputNumber/CustomInputNumber";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { canEditTable } from "./PermissionCheck/PermissionCheck";
import { reportFlareStore } from "src/stores/report-flare-volume-store";
import { formatNumber, formatNumberWithCommas } from "src/utils/process-number";
import { onKeyDownNumberWithFixedDecimal } from "src/utils/key-down-validate";

const { Text } = Typography;
interface FlareCompoundTableProps {}

const FlareCompoundTable: React.FC<FlareCompoundTableProps> = ({}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [formSettingFlareCompound] = Form.useForm();
  const [visibleModal, setVisibleModal] = useState(false);
  const getSettingFlareCompound = settingStore(useShallow((state) => state.getSettingFlareCompound));
  const dataSettingFlareCompound = settingStore(useShallow((state) => state.dataSettingFlareCompound));
  const totalDocs = dataSettingFlareCompound.total;
  const [isEdit, setIsEdit] = useState<UpdateSettingFlareCompound | null>(null);
  const { permissions } = useNavigationMenu();

  const getFilterCompoundColumn = settingStore(useShallow((state) => state.getFilterCompoundColumn));
  const filterFlareCompound = settingStore(useShallow((state) => state.filterFlareCompound));
  const loadingManageFlareCompound = settingStore(useShallow((state) => state.loadingManageFlareCompound));
  const createSettingFlareCompound = settingStore(useShallow((state) => state.createSettingFlareCompound));
  const deleteSettingFlareCompound = settingStore(useShallow((state) => state.deleteSettingFlareCompound));
  const updateSettingFlareCompound = settingStore(useShallow((state) => state.updateSettingFlareCompound));
  const clearErrorSettingFlareCompound = settingStore(useShallow((state) => state.clearErrorSettingFlareCompound));
  const errorManageFlareCompound = settingStore(useShallow((state) => state.errorManageFlareCompound));
  const calculateReportAfterUpdateMasterFlareCompound = reportFlareStore(
    useShallow((state) => state.calculateReportAfterUpdateMasterFlareCompound),
  );

  const onClickEdit = (record: ResponseSettingFlareCompound) => {
    setIsEdit(record);
    setVisibleModal(true);
    formSettingFlareCompound.setFieldsValue({
      ...record,
    });
  };
  const deleteSetting = async (id: string) => {
    const confirm = await VocsModal.delete({
      message: "",
    });

    if (!confirm) return;
    deleteSettingFlareCompound(id).then(() => onFinishAction());
  };

  const {
    onChange,
    filter,
    page,
    setPage,
    loading,
    limit: pageSize,
    setLimit: setPageSize,
    data: sourceData,
  } = usePagination<ResponseSettingFlareCompound, FilterSettingFlareCompound>({
    action: getSettingFlareCompound,
    remoteData: dataSettingFlareCompound.data,
  });

  const compoundFilter = useMemo(() => {
    return filterFlareCompound.compound.map((item) => {
      return {
        text: item,
        value: item,
      };
    });
  }, [filterFlareCompound.compound]);
  const kcalM3Filter = useMemo(() => {
    return filterFlareCompound.kcalM3.map((item) => {
      return {
        text: item,
        value: item,
      };
    });
  }, [filterFlareCompound.kcalM3]);
  const kcalKgFilter = useMemo(() => {
    return filterFlareCompound.kcalKg.map((item) => {
      return {
        text: item,
        value: item,
      };
    });
  }, [filterFlareCompound.kcalKg]);
  const remarkFilter = useMemo(() => {
    return filterFlareCompound.remark.map((item) => {
      return {
        text: item,
        value: item,
      };
    });
  }, [filterFlareCompound.remark]);

  const handleTableChange: TableProps<ResponseSettingFlareCompound>["onChange"] = (_, filters) => {
    onChange(filters);
  };

  const getIndex = (index: number) => (page - 1) * pageSize + index;
  const columns: TableProps<ResponseSettingFlareCompound>["columns"] = [
    {
      title: "No",
      key: "no",
      render: (_, __, index) => getIndex(index + 1),
      align: "center",
    },
    {
      title: "Compound",
      dataIndex: "compound",
      key: "compound",
      render: (text) => <Text>{text}</Text>,
      filters: compoundFilter,
      align: "left",
    },
    {
      title: "HTV (Kcal/M3)",
      dataIndex: "kcalM3",
      key: "kcalM3",
      render: (text) => <Text>{formatNumberWithCommas(text)}</Text>,
      filters: kcalM3Filter,
      align: "left",
    },
    {
      title: "HTV (Kcal/Kg)",
      dataIndex: "kcalKg",
      key: "kcalKg",
      render: (text) => <Text>{formatNumberWithCommas(text)}</Text>,
      filters: kcalKgFilter,
      align: "left",
    },
    {
      title: "Remark",
      dataIndex: "remark",
      key: "remark",
      render: (text) => <Text>{text}</Text>,
      filters: remarkFilter,
      align: "left",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle" direction={"horizontal"}>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            onClick={() => onClickEdit(record)}
            disabled={!canEditTable(permissions)}
          >
            <BsPencilSquare size={18} />
          </Button>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            onClick={() => deleteSetting(record._id)}
            disabled={!canEditTable(permissions)}
          >
            <BsTrashFill size={18} />
          </Button>
        </Space>
      ),
      align: "center",
    },
  ];

  const getColumnFilter = () => {
    getFilterCompoundColumn("compound");
    getFilterCompoundColumn("kcalM3");
    getFilterCompoundColumn("kcalKg");
    getFilterCompoundColumn("remark");
  };

  useDidMount(() => {
    getColumnFilter();
    getSettingFlareCompound({ ...filter, page, limit: pageSize });
  });

  const onChangePage = (val: number) => {
    setPage(val);
  };

  const paginationProps: PaginationProps = {
    size: "small",
    pageSize: pageSize,
    current: page,
    total: totalDocs || 0,
    defaultCurrent: 1,
    onChange: onChangePage,
    showSizeChanger: false,
  };

  const onAddFlareInfo = () => {
    setVisibleModal(true);
  };

  const closeModal = () => {
    setVisibleModal(false);
    formSettingFlareCompound.resetFields();
    clearErrorSettingFlareCompound();
    setIsEdit(null);
  };

  const submitSettingFlareCompound = async () => {
    let checkError = false;
    await formSettingFlareCompound.validateFields().catch(() => {
      checkError = true;
    });
    if (checkError) return;

    const formData = formSettingFlareCompound.getFieldsValue();
    // console.log("form data ; ", formData);
    const formatData = replaceUndefinedWithNull(formData);
    const finalData = trimAllKey(formatData, ["compound"]) as CreateSettingFlareCompound;
    if (isEdit) {
      updateSettingFlareCompound({ ...finalData, _id: isEdit._id }).then(onFinishAction);
    } else createSettingFlareCompound(finalData).then(onFinishAction);
  };

  const onFinishAction = (errorMessage?: unknown) => {
    // console.log("errorMessage : ", errorMessage);
    if (errorMessage) {
      formSettingFlareCompound.setFields([
        {
          name: "compound",
          errors: [""],
        },
        {
          name: "kcalKg",
          errors: [""],
        },
        {
          name: "kcalM3",
          errors: [""],
        },
      ]);
      return;
    } else {
      if (isEdit?._id) {
        calculateReportAfterUpdateMasterFlareCompound(isEdit._id);
      }
      messageApi.open({
        type: "success",
        content: CommonSuccess.SAVED_SUCCESSFULL,
      });
      getSettingFlareCompound({ ...filter, page, limit: pageSize });
      getColumnFilter();
      closeModal();
    }
  };

  // console.log("filterFlareCompound : ", filterFlareCompound);

  return (
    <Row className="w-full" id={"wrap-combustion-config-table"}>
      {contextHolder}
      {canEditTable(permissions) && (
        <Row className="w-full" justify={"end"}>
          <Button
            type={"primary"}
            size={"middle"}
            className="flex items-center"
            onClick={onAddFlareInfo}
            disabled={!canEditTable(permissions)}
          >
            <PlusCircleOutlined />
            New
          </Button>
        </Row>
      )}
      <Table
        columns={columns}
        dataSource={sourceData as ResponseSettingFlareCompound[]}
        className="w-full"
        loading={loading}
        pagination={false}
        onChange={handleTableChange}
      />

      <Row className="w-full mt-4" justify={"end"}>
        <Pagination {...paginationProps} />
      </Row>

      <Modal
        title={<ModalTitle title="Compound" />}
        open={visibleModal}
        width={1000}
        onCancel={closeModal}
        footer={
          <Row className="w-full border-t" justify={"end"}>
            <Button className="mt-4" key="submit" type="primary" loading={loadingManageFlareCompound} onClick={submitSettingFlareCompound}>
              Save
            </Button>
          </Row>
        }
        maskClosable={false}
        centered
      >
        {errorManageFlareCompound && (
          <Row className="w-full" justify={"center"}>
            <Text type="danger">{errorManageFlareCompound}</Text>
          </Row>
        )}
        <Form
          layout={"vertical"}
          name={"setting-flare-compound"}
          autoComplete="off"
          form={formSettingFlareCompound}
          requiredMark={false}
          onFieldsChange={(_) => {
            if (errorManageFlareCompound) {
              clearErrorSettingFlareCompound();
              formSettingFlareCompound.setFields([
                {
                  name: "compound",
                  errors: undefined,
                },
                {
                  name: "kcalKg",
                  errors: undefined,
                },
                {
                  name: "kcalM3",
                  errors: undefined,
                },
              ]);
            }
          }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label={<FormLabel label={"Compound"} required />}
                name="compound"
                rules={[
                  {
                    required: true,
                    message: generateErrorMessage("Compound"),
                  },
                ]}
              >
                <Input maxLength={50} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6} lg={6}>
              <Form.Item
                label={<FormLabel label={"HTV"} required />}
                name="kcalKg"
                rules={[
                  {
                    required: true,
                    message: generateErrorMessage("Kcal/Kg"),
                  },
                ]}
              >
                <InputNumber
                  addonAfter={"Kcal/Kg"}
                  onKeyDown={(e) => {
                    onKeyDownNumberWithFixedDecimal(e, 0);
                  }}
                  formatter={(value) => formatNumber(value as number, 0)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6} lg={6}>
              <Form.Item
                label=" "
                name="kcalM3"
                rules={[
                  {
                    required: true,
                    message: generateErrorMessage("Kcal/M3"),
                  },
                ]}
              >
                <InputNumber
                  addonAfter={"Kcal/M3"}
                  onKeyDown={(e) => {
                    onKeyDownNumberWithFixedDecimal(e, 0);
                  }}
                  formatter={(value) => formatNumber(value as number, 0)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={12}>
              <Form.Item label="Remark" name="remark">
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Row>
  );
};

export default FlareCompoundTable;
